import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader } from '@altermaind/web/ui';
import { AlertBadge, GoBack, LayoutContext, Tabs } from '@/_shared/components';
import { FeatureFlag } from '@/_shared/constants/featureFlags';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { useTabs } from '@/_shared/hooks/useTabs';
import { TabAssessment, TabCredits, TabProfile } from '../components';
import { getEmployerByIdCall } from '../state';

export const PeopleDetailsScreen = () => {
  const t = useTranslations('people.details.tabs');
  const { isFeatureEnabled } = useFeatureFlags();
  const creditsFF = isFeatureEnabled(FeatureFlag.CREDITS);

  const tShared = useTranslations();
  const { setLayout } = useContext(LayoutContext);
  const { peopleId } = useParams();
  const { isDesktop } = useBreakingPoints();
  const [isEditing, setIsEditing] = useState(false);

  const {
    execute: executeGetEmployerById,
    loading: loadingGetEmployerById,
    error: errorGetEmployerById,
    success: successGetEmployerById,
    employer,
  } = getEmployerByIdCall.hook();

  const tabsInitialState = creditsFF
    ? [
        {
          title: t('tab1'),
          index: 0,
          active: true,
          dataTestId: 'profile',
        },
        {
          title: t('tab2'),
          index: 1,
          active: false,
          dataTestId: 'assessment',
        },
        {
          title: t('tab3'),
          index: 2,
          active: false,
          dataTestId: 'credits',
        },
      ]
    : [
        {
          title: t('tab1'),
          index: 0,
          active: true,
          dataTestId: 'profile',
        },
        {
          title: t('tab2'),
          index: 1,
          active: false,
          dataTestId: 'assessment',
        },
      ];

  const { tabs, setOpenTab } = useTabs({
    tabsInitialState,
  });

  useEffect(() => {
    if (peopleId) {
      executeGetEmployerById({ employeeId: peopleId });
    }
  }, [executeGetEmployerById, peopleId]);

  useEffect(() => {
    setLayout({ title: 'people' });
  }, [setLayout, t]);

  if (loadingGetEmployerById) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetEmployerById) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (successGetEmployerById && !loadingGetEmployerById && !errorGetEmployerById && employer) {
    return (
      <Box>
        <GoBack />
        <Box>
          {!isEditing && (
            <Box overflowX={isDesktop ? 'auto' : 'scroll'}>
              <Tabs buttonVariant='primary' setActiveTab={setOpenTab} tabs={tabs} />
            </Box>
          )}
          {tabs.map(tab => {
            return (
              <Box key={tab.title} display='flex' flexDirection='column'>
                {tab.index === 0 && tab.active && (
                  <TabProfile employer={employer} onEditChange={setIsEditing} />
                )}
                {tab.index === 1 && tab.active && <TabAssessment employer={employer} />}
                {tab.index === 2 && tab.active && <TabCredits employer={employer} />}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return null;
};
